import React, { useState, useEffect } from 'react'
import { Segment, Header, Loader, Icon, Grid } from 'semantic-ui-react'
import i18next from 'i18next'
import request from 'app/common/hoc/request/request'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import advagymA from 'assets/images/advagym_a_white.svg'
import Badge from 'app/protected/roles/common/Badge'
import excercisesSvgWhite from 'assets/images/excercises_white.svg'
import dumbellSvgWhite from 'assets/images/dumbell_white.svg'
import _get from 'lodash/get'

interface MatchParams {
  id?: string
}

interface ExportUserMedia {
  name?: string
  url?: string
}

interface ExportUser {
  name?: string
  media?: ExportUserMedia[] | null
}

interface ExportUserDataState {
  user: ExportUser | null
  ongoing: boolean
  valid: boolean
  error: string | null
}

interface ExportUserDataProps extends RouteComponentProps<MatchParams> {
  code: string
}

interface ValidityResponse {
  valid: boolean
  user: ExportUser
}

interface RequestHandle {
  promise: Promise<unknown>
}

interface RequestError {
  isCanceled?: boolean
  response?: { data: { error?: string } }
}

interface LegacyNavigator extends Navigator {
  msSaveOrOpenBlob?: (blob: Blob) => boolean
  msSaveBlob?: (blob: Blob, defaultName?: string) => boolean
}

const isRequestError = (value: unknown): value is RequestError =>
  typeof value === 'object' && value !== null

const hasLegacySaveBlob = (nav: Navigator): nav is LegacyNavigator =>
  'msSaveOrOpenBlob' in nav && 'msSaveBlob' in nav

const ExportUserData: React.FC<ExportUserDataProps> = (props) => {
  const { code } = props

  const initialState: ExportUserDataState = {
    user: null,
    ongoing: false,
    valid: false,
    error: null,
  }

  const [{ user, ongoing, valid, error }, setState] = useState(initialState)

  useEffect(() => {
    if (code && code !== '') {
      void checkValidity()
    }
  }, [])

  let currentRequest: RequestHandle | null = null

  const checkValidity = async () => {
    setState((prevState) => ({ ...prevState, ongoing: true }))

    try {
      currentRequest = request({
        method: 'get',
        url: `/api/web/export/user/code?code=${code}`,
      })
      const response = (await currentRequest.promise) as {
        data: ValidityResponse
      }
      const { valid: isValid, user: userData } = response.data
      setState((prevState) => ({
        ...prevState,
        ongoing: false,
        valid: isValid,
        user: userData,
        error: null,
      }))
    } catch (err: unknown) {
      if (!isRequestError(err) || err.isCanceled) return
      if (err.response) {
        console.debug('ExportUserData check validity failed - 1', err.response)
        const msg = err.response.data.error || 'unknown response error'
        setState((prevState) => ({
          ...prevState,
          ongoing: false,
          valid: false,
          error: msg,
        }))
      } else {
        console.debug('ExportUserData check validity failed - 2', err)
        setState((prevState) => ({
          ...prevState,
          ongoing: false,
          valid: false,
          error: 'unknown error',
        }))
      }
    }
  }

  const sanitizeForFilename = (str: string) => {
    const cleaned = str
      .split('')
      .filter((ch) => {
        const code = ch.codePointAt(0)
        return (
          code !== undefined && code > 31 && !String.raw`<>:"/\|?*`.includes(ch)
        )
      })
      .join('')
      .trim()
    return cleaned || 'export'
  }

  const downloadCSV = (data: string, userName: string, title: string) => {
    const filename =
      sanitizeForFilename(userName).replaceAll(/\s/g, '').toLowerCase() +
      '__' +
      sanitizeForFilename(title).replaceAll(/\s/g, '_') +
      '.csv'

    const blob = new Blob(['\ufeff', data])

    if (hasLegacySaveBlob(globalThis.navigator))
      // IE hack see http://msdn.microsoft.com/en-us/library/ie/hh779016.aspx
      globalThis.navigator.msSaveBlob(blob, filename)
    else {
      const a = globalThis.document.createElement('a')
      a.href = globalThis.URL.createObjectURL(blob)
      a.download = filename
      globalThis.document.body.appendChild(a)
      a.click() // IE: "Access is denied" see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
      a.remove()
    }
  }

  const downloadCsv = async (type: string, title: string) => {
    setState((prevState) => ({ ...prevState, ongoing: true }))

    try {
      currentRequest = request({
        method: 'get',
        url: `/api/web/export/user/${code}?content=${type}`,
      })
      const response = (await currentRequest.promise) as { data: string }
      downloadCSV(response.data, _get(user, 'name', 'user'), title)
      setState((prevState) => ({
        ...prevState,
        ongoing: false,
      }))
    } catch (err: unknown) {
      if (!isRequestError(err) || err.isCanceled) return
      if (err.response) {
        console.debug('ExportUserData workouts failed - 1', err.response)
        const msg = err.response.data.error || 'unknown response error'
        setState((prevState) => ({
          ...prevState,
          ongoing: false,
          error: msg,
        }))
      } else {
        console.debug('ExportUserData workouts failed - 2', err)
        setState((prevState) => ({
          ...prevState,
          ongoing: false,
          error: 'unknown error',
        }))
      }
    }
  }

  const headerView = () => {
    const media = user ? user.media : null
    const logo = media ? media.find((m) => m.name === 'icon') || null : null

    return (
      <Grid>
        <Grid.Row textAlign="center" className="pb-0">
          <Grid.Column textAlign="center">
            <div className="flex justify-center">
              <Badge
                type="coach"
                label={_get(user, 'name', 'User')}
                icon=""
                customIcon={logo ? '' : advagymA}
                size="big"
                imageUrl={logo ? logo.url || '' : ''}
              />
            </div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row textAlign="center">
          <Grid.Column>
            <Header as="h2" icon>
              <div>
                {i18next.t('welcome') + ' '}
                {_get(user, 'name', 'User')}
              </div>
            </Header>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    )
  }

  const item = (
    action: () => void,
    icon: string,
    title: string,
    subtitle: string
  ) => {
    return (
      <Grid.Column mobile="16" tablet="8" computer="8" className="mb-2.5">
        <Segment
          onClick={() => action()}
          className="cursor-pointer flex justify-start items-center p-[15px] rounded"
        >
          <span>
            <Badge type="coach" customIcon={icon} />
          </span>

          <span className="inline-flex flex-col pl-3 flex-1">
            <div>
              <Header as="h4">{title}</Header>
            </div>
            <div>
              <span className="text-muted">{subtitle}</span>
            </div>
          </span>
        </Segment>
      </Grid.Column>
    )
  }

  if (ongoing) {
    return (
      <>
        <Header textAlign="center" as="h2">
          {i18next.t('fetching') + '...'}
        </Header>
        <Loader
          size="massive"
          className="workaround"
          inline="centered"
          active
        />
      </>
    )
  }

  if (!valid || error) {
    return (
      <div className="flex flex-1 justify-center">
        <Header as="h2" icon>
          <Icon name="warning sign" />
          {i18next.t('link_has_expired_or_not_valid')}
          <Header.Subheader>
            {i18next.t('link_expired_open_app_and_export_again')}
          </Header.Subheader>
        </Header>
      </div>
    )
  }

  return (
    <Grid>
      <Grid.Row columns={1}>
        <Grid.Column>{headerView()}</Grid.Column>
      </Grid.Row>
      <Grid.Row centered>
        {item(
          () => downloadCsv('workouts', i18next.t('workout_history_title')),
          excercisesSvgWhite,
          i18next.t('workout_history_title'),
          i18next.t('export_to_csv')
        )}
        {item(
          () => downloadCsv('routines', i18next.t('workout_templates_title')),
          dumbellSvgWhite,
          i18next.t('workout_templates_title'),
          i18next.t('export_to_csv')
        )}
      </Grid.Row>
    </Grid>
  )
}

const mapStateToProps = (
  _state: unknown,
  props: RouteComponentProps<MatchParams>
) => {
  const { match } = props
  const code = match.params.id ? match.params.id : ''

  return {
    code,
  }
}

const ConnectedExportUserData = connect(mapStateToProps)(
  ExportUserData as React.ComponentType<ExportUserDataProps>
)

export default withRouter(ConnectedExportUserData)
